import { useCallback, useEffect, useRef, useState } from "react";

import { KVNodeEdit } from "./kvStoreSeed";

// One row being added — under a parent, or at the top level (parentKey
// null) — before any message exists for it: both fields type as plain
// text, and an empty key discards the add.
export type NodeAdding = {
  parentKey: string | null;
  key: string;
  value: string;
};

// The add row's lifecycle: open under a parent or at the root, track its
// key/value pair, apply on a click outside or a Tab out, close.
const useNodeAdding = (
  editNode: (key: string, edit: KVNodeEdit) => void,
  delimiter: string | null
) => {
  const [adding, setAdding] = useState<NodeAdding | null>(null);
  const rowRef = useRef<HTMLTableRowElement>(null);

  // Applies the add — the new child is just a value at the joined key. A
  // blank key discards it.
  const applyAdding = useCallback(() => {
    if (!adding) {
      return;
    }
    setAdding(null);
    const key = adding.key.trim();
    if (key) {
      editNode(
        adding.parentKey === null
          ? key
          : `${adding.parentKey}${delimiter ?? "."}${key}`,
        { value: adding.value }
      );
    }
  }, [adding, delimiter, editNode]);

  useEffect(() => {
    if (!adding) {
      return;
    }
    // Clicking outside the pair of inputs applies the add.
    const handleMouseDown = (event: MouseEvent) => {
      if (rowRef.current?.contains(event.target as Node)) {
        return;
      }
      applyAdding();
    };
    document.addEventListener("mousedown", handleMouseDown);
    return () => document.removeEventListener("mousedown", handleMouseDown);
  }, [adding, applyAdding]);

  return {
    adding,
    rowRef,
    startAdding: (parentKey: string | null) =>
      setAdding({ parentKey, key: "", value: "" }),
    changeAdding: (patch: Partial<Pick<NodeAdding, "key" | "value">>) =>
      setAdding((current) => current && { ...current, ...patch }),
    applyAdding,
  };
};

export type NodeAdder = ReturnType<typeof useNodeAdding>;

export default useNodeAdding;
